import React from 'react';
import { trackEvent } from '../utils/tracking';

/**
 * /medical-staffing
 * One-pager landing page for the medical staffing consultant email campaign.
 * Primary conversion is the Calendly CTA, not self-serve signup.
 */
const CALENDLY_URL = 'https://calendly.com/knine';

const MedicalStaffing = () => {
  const handleCalendly = (loc) => trackEvent('calendly_click', { location: loc });

  return (
    <>
      {/* Hero */}
      <section className="hero">
        <div className="paw-decoration" style={{ top: '15%', left: '5%' }}>🐾</div>
        <div className="paw-decoration" style={{ top: '25%', right: '8%', animationDelay: '1s' }}>🐾</div>

        <div className="container hero-content fade-up" style={{ maxWidth: '780px' }}>
          <div className="section-badge blue">🏥 Built for Medical Staffing Consultants</div>
          <h1 className="hero-title" style={{ fontSize: '2.6rem', lineHeight: 1.15 }}>
            Fill Travel Nurse &amp; Allied Health Roles <span className="text-gradient">10x Faster</span>
          </h1>
          <p className="hero-subtitle" style={{ fontSize: '1.1rem', maxWidth: '640px', margin: '0 auto 28px' }}>
            kNine's AI sourcing agents find clinicians across 100M+ records and verify every email and
            phone number in real time — so your desk spends the day placing candidates, not chasing
            bounced outreach.
          </p>

          <div className="hero-actions" style={{ marginBottom: '32px' }}>
            <a
              href={CALENDLY_URL}
              className="btn btn-gradient btn-lg"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleCalendly('hero')}
            >
              Book a 15-Minute Call
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </div>

          <div className="hero-stats fade-up delay-3" style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div className="hero-stat">
              <div className="hero-stat-value">100M+</div>
              <div className="hero-stat-label">Contact Records</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-value">95%</div>
              <div className="hero-stat-label">Email Accuracy</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-value">10x</div>
              <div className="hero-stat-label">Faster Sourcing</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-value">Real-Time</div>
              <div className="hero-stat-label">Contact Verification</div>
            </div>
          </div>
        </div>
      </section>

      {/* The Reality of Medical Staffing */}
      <section style={{ paddingTop: '48px', paddingBottom: '48px' }}>
        <div className="container" style={{ maxWidth: '860px' }}>
          <h2 className="section-title text-center" style={{ fontSize: '1.6rem', marginBottom: '32px' }}>
            The Reality of Medical Staffing
          </h2>
          <div className="feature-grid" style={{ maxWidth: '860px', margin: '0 auto' }}>
            {[
              {
                icon: '⏱️',
                title: 'Contracts Move in Days, Not Weeks',
                desc: 'Travel nursing, per diem, and locum roles close fast. Every hour spent chasing a disconnected number or a bounced email is a shift that goes to a competing desk.',
              },
              {
                icon: '📵',
                title: 'Clinicians Are Hard to Reach',
                desc: 'Nurses and allied health professionals change numbers and emails between assignments constantly. Generic databases go stale long before you ever dial.',
              },
              {
                icon: '🗂️',
                title: 'Your ATS Only Knows Who You\'ve Already Found',
                desc: 'Keeping a pipeline full for surge staffing and short-notice coverage means a constant stream of net-new, verified candidates — not just your existing pool.',
              },
            ].map((item) => (
              <div key={item.title} className="card" style={{ padding: '28px' }}>
                <div style={{ fontSize: '2rem', marginBottom: '12px' }}>{item.icon}</div>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '8px' }}>
                  {item.title}
                </h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7, margin: 0 }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How kNine Helps */}
      <section className="bg-off-white" style={{ paddingTop: '48px', paddingBottom: '48px' }}>
        <div className="container" style={{ maxWidth: '860px' }}>
          <h2 className="section-title text-center" style={{ fontSize: '1.6rem', marginBottom: '12px' }}>
            How kNine Fills Your <span className="text-gradient">Pipeline</span>
          </h2>
          <p className="section-subtitle text-center mx-auto" style={{ marginBottom: '36px' }}>
            Purpose-built AI sourcing that plugs into how your desk already works.
          </p>

          <div className="feature-grid-2" style={{ maxWidth: '860px', margin: '0 auto' }}>
            {[
              {
                icon: '🤖',
                title: 'AI Search Built for Clinical Roles',
                desc: 'Describe the specialty, license, shift, and location you need in plain English. Roxi finds matching clinicians across 100M+ records — no boolean strings required.',
              },
              {
                icon: '✅',
                title: 'Every Contact kNine Verified™',
                desc: 'Every email is checked for deliverability in real time, and every phone number is checked for type and carrier via Twilio Lookup — so your first outreach reaches a real, working line.',
              },
              {
                icon: '🔗',
                title: 'Pushes Straight to Your ATS',
                desc: 'Sourced, verified candidates flow directly into PCRecruiter, Crelate, BigBiller/TE Recruit, and more. No manual re-entry between finding a candidate and calling them.',
              },
              {
                icon: '⚡',
                title: 'Built for the Speed of Staffing',
                desc: 'A Chrome extension plus AI search means your team can source and reach out in minutes between shifts, callouts, and coverage gaps.',
              },
            ].map((item) => (
              <div key={item.title} className="card" style={{ padding: '28px' }}>
                <div style={{ fontSize: '1.75rem', marginBottom: '12px' }}>{item.icon}</div>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '8px' }}>
                  {item.title}
                </h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7, margin: 0 }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Verified callout */}
          <div className="card-flat" style={{
            maxWidth: '700px',
            margin: '32px auto 0',
            padding: '24px 32px',
            textAlign: 'center',
            background: 'var(--primary-bg)',
            border: '1px solid #dbeafe'
          }}>
            <p style={{ fontSize: '0.9rem', color: 'var(--text)', lineHeight: 1.7, margin: 0 }}>
              <strong style={{ color: 'var(--primary-dark)' }}>No more dialing dead numbers.</strong>{' '}
              kNine Verified™ checks deliverability and phone type/carrier on every single contact —
              across every plan, on every search.
            </p>
          </div>
        </div>
      </section>

      {/* Mid CTA */}
      <section style={{ paddingTop: '48px', paddingBottom: '48px' }}>
        <div className="container" style={{ maxWidth: '680px', textAlign: 'center' }}>
          <h2 className="section-title" style={{ fontSize: '1.5rem', marginBottom: '12px' }}>
            See It Work on Your Desk
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '24px' }}>
            15 minutes. No slide deck — just a live look at how fast your next req could fill.
          </p>
          <a
            href={CALENDLY_URL}
            className="btn btn-gradient btn-lg"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleCalendly('mid')}
          >
            Book a 15-Minute Call
          </a>
        </div>
      </section>

      {/* Contact / Final CTA */}
      <section>
        <div className="container">
          <div className="cta-section">
            <div className="cta-paw" style={{ top: '-20px', left: '-10px', transform: 'rotate(-25deg)' }}>🐾</div>
            <div className="cta-paw" style={{ bottom: '-20px', right: '-10px', transform: 'rotate(15deg)' }}>🐾</div>
            <h2>Let's Talk About Your Desk</h2>
            <p>
              Tell me about your book of business and I'll show you exactly how kNine fits in —
              no pressure, no obligation.
            </p>
            <div style={{ marginBottom: '28px' }}>
              <a
                href={CALENDLY_URL}
                className="btn btn-white btn-lg"
                style={{ border: 'none' }}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleCalendly('final')}
              >
                Schedule a 15-Minute Call
              </a>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '28px',
              flexWrap: 'wrap',
              fontSize: '0.95rem',
              opacity: 0.95,
            }}>
              <span><strong>Chris Cyrus</strong> · VP of Sales</span>
              <a href="mailto:chris@knine.ai" style={{ color: 'white', textDecoration: 'underline' }}>
                chris@knine.ai
              </a>
              <a href="tel:4404854497" style={{ color: 'white', textDecoration: 'underline' }}>
                (440) 485-4497
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default MedicalStaffing;
